/* ============================================================================
   cl-motion.js — scroll-reveal engine for the newStyle theme
   ----------------------------------------------------------------------------
   Loaded ONCE (deferred) from snippets/cl-motion.liquid, which theme.liquid
   renders in <head>. Never render it per section: a 24-card PLP must not carry
   24 copies of this file (same lesson as snippets/cl-card-slider.liquid).

   Contract with cl-motion.css:
     - html.cl-motion .......... hidden state is active (set by the inline guard)
     - .is-in .................. element has entered view, play the transition
     - --cl-delay .............. per-element stagger offset, set here

   Public API (window.clMotion) exists for client-rendered DOM — cl-wishlist
   builds its cards in JS after Swym resolves, so nothing there is in the
   document when this runs. Call clMotion.register(container) after injecting.
   ============================================================================ */
(function () {
  'use strict';

  var REVEAL = '[data-cl-reveal]';
  var STAGGER = '[data-cl-stagger]';
  var ROOT_CLASS = 'cl-motion';

  /* Fire when 15% of the element is showing, and pull the trigger line 5% up
     from the bottom edge so a reveal never starts while it is still visually
     off-screen (which reads as "already there" by the time you scroll to it). */
  var THRESHOLD = 0.15;
  var ROOT_MARGIN = '0px 0px -5% 0px';

  /* Stagger stops growing after this many items. Without a cap a 24-card grid
     ends on a 1.7s delay and the last row looks broken rather than sequenced. */
  var STAGGER_CAP = 6;
  var STAGGER_DEFAULT = 70;

  var html = document.documentElement;

  /** Last resort: show everything, unconditionally. */
  function revealAll(scope) {
    var nodes = (scope || document).querySelectorAll(REVEAL);
    for (var i = 0; i < nodes.length; i++) nodes[i].classList.add('is-in');
  }

  /** Nothing is hidden in the first place — drop the guard and stand down. */
  function standDown() {
    html.classList.remove(ROOT_CLASS);
    revealAll();
  }

  var reduced = false;
  try {
    reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch (e) {
    /* matchMedia is ancient and universal, but never let a probe be the thing
       that leaves the storefront blank. */
  }

  if (reduced || !('IntersectionObserver' in window) || !html.classList.contains(ROOT_CLASS)) {
    standDown();
    return;
  }

  var io = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      if (!entry.isIntersecting) continue;
      io.unobserve(entry.target); // reveals are once-only; nothing re-animates on scroll-up
      if (entry.target.hasAttribute('data-cl-stagger')) revealGroup(entry.target);
      else entry.target.classList.add('is-in');
    }
  }, { threshold: THRESHOLD, rootMargin: ROOT_MARGIN });

  /**
   * A staggered container reveals its children as one cascade, triggered by the
   * CONTAINER entering view — not by each child independently. In a grid the
   * children cross the trigger line at the same moment anyway, so observing
   * them separately produces a ragged near-simultaneous flicker instead of a
   * sequence.
   */
  function revealGroup(container) {
    var step = parseInt(container.getAttribute('data-cl-stagger'), 10);
    if (!isFinite(step) || step < 0) step = STAGGER_DEFAULT;
    /* A container can be both the group and a reveal target (e.g. a section that
       fades in while its cards cascade). querySelectorAll excludes the root, so
       reveal it explicitly or it stays at opacity 0 forever. */
    if (container.hasAttribute('data-cl-reveal')) container.classList.add('is-in');
    var items = container.querySelectorAll(REVEAL);
    for (var i = 0; i < items.length; i++) {
      items[i].style.setProperty('--cl-delay', Math.min(i, STAGGER_CAP) * step + 'ms');
      items[i].classList.add('is-in');
    }
  }

  /**
   * Observe everything in `scope` that isn't already spoken for.
   * Safe to call repeatedly — IntersectionObserver.observe() on an element it
   * already watches is a no-op, and revealed elements were unobserved.
   */
  /**
   * Has this element a layout box at all?
   *
   * getClientRects() is empty only when the element — or ANY ancestor — is
   * display:none. Off-screen elements still have rects, and visibility:hidden
   * still has rects, so this isolates exactly the "not rendered" case.
   */
  function isRendered(el) {
    return el.getClientRects().length > 0;
  }

  /**
   * A display:none element is never intersected, so it would keep opacity 0
   * forever — and the moment something reveals it (a media query on resize, a
   * tab, JS) it would appear INVISIBLE. Found on the PDP: the "sell with us"
   * promo is mobile-only, so on desktop it sat permanently unrevealed.
   *
   * Settle it immediately instead. It cannot be seen right now, so there is
   * nothing to animate and nothing is lost — but if it is ever shown it is
   * already in its final state. Costs nothing; removes a whole bug class.
   */
  function observeOrSettle(el) {
    if (!isRendered(el)) {
      if (el.hasAttribute('data-cl-stagger')) revealGroup(el);
      else el.classList.add('is-in');
      return;
    }
    io.observe(el);
  }

  function register(scope) {
    var root = scope || document;

    if (root.nodeType === 1 && root.matches && root.matches(STAGGER)) observeOrSettle(root);
    var groups = root.querySelectorAll(STAGGER);
    for (var i = 0; i < groups.length; i++) observeOrSettle(groups[i]);

    var solo = root.querySelectorAll(REVEAL);
    for (var j = 0; j < solo.length; j++) {
      if (solo[j].closest(STAGGER)) continue; // a group owns this one
      observeOrSettle(solo[j]);
    }
  }

  function safeRegister(scope) {
    try {
      register(scope);
    } catch (e) {
      /* A selector or DOM surprise must never cost the merchant their content. */
      standDown();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { safeRegister(); });
  } else {
    safeRegister();
  }

  /* Theme editor: Shopify swaps the section's DOM on every setting change, so
     the new nodes start hidden and are watched by nobody. Without this, editing
     any section makes its content vanish for whoever is in the customizer. */
  document.addEventListener('shopify:section:load', function (e) { safeRegister(e.target); });

  /* ── Exit collapse ────────────────────────────────────────────────────────
     Shared by the cart page and the cart drawer: a removed line fades, shrinks
     slightly and closes its own height, so the rows below travel up to fill the
     gap instead of jumping. It lives here rather than in either section because
     both surfaces need the identical motion — removing an item should look the
     same wherever it is done from — and this file is already the theme's one
     motion engine, loaded on every page.

     REVERSIBLE BY DESIGN. It is a CSS class, not a filled Web Animation: the
     drawer's remove is owned by the agency's script and cannot report failure,
     so its pending state is released on a timeout — and when that happens the
     row has to come back. release() undoes everything this sets.

     max-height, not height: the row's natural height is not a stated value, and
     a transition from `none` animates nothing. The measured height goes on as an
     inline style first, which the class then overrides with !important. */
  var POP_CLASS = 'cl-exit-pop';
  var COLLAPSE_CLASS = 'cl-exit-collapse';
  var POP_MS = 160;
  var DROP_MS = 380;
  var COLLAPSE_MS = POP_MS + DROP_MS;

  function collapse(el, done) {
    if (!el) { if (done) done(); return; }
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    /* Reduced motion still gets the removal, just not the travel: no transition
       runs, so the callback must not wait on one that will never fire. */
    if (reduced) {
      el.style.display = 'none';
      if (done) done();
      return;
    }
    el.style.maxHeight = el.getBoundingClientRect().height + 'px';
    /* Read forces the layout that makes the starting value real; without it the
       browser coalesces both states into one frame and nothing animates. */
    void el.offsetHeight;
    /* TWO STAGES, and they need two classes: the row lifts off the page first, then
       drops away as it fades. A single transition cannot go up and then down — the
       property would only ever travel to its one target value — so the stages are
       sequenced here and each owns its own timing in cl-styles.css. */
    el.classList.add(POP_CLASS);
    setTimeout(function () {
      /* Bail if something released the row while it was still on the way up. */
      if (el.classList.contains(POP_CLASS)) el.classList.add(COLLAPSE_CLASS);
    }, POP_MS);
    if (done) setTimeout(done, COLLAPSE_MS);
  }

  function release(el) {
    if (!el) return;
    el.classList.remove(POP_CLASS);
    el.classList.remove(COLLAPSE_CLASS);
    el.style.maxHeight = '';
    el.style.display = '';
  }

  /* ── Fly to a header icon ─────────────────────────────────────────────────
     An icon arcs from a button up to one of the header's icons, growing on the way out
     and shrinking into it, which then bumps. Three callers, one motion, so none of them
     can drift: the PDP's Add to cart (CL_PDP_FLY_TO_CART in cl-product.liquid), every
     card's quick add (cl-quickadd.js) and the card wishlist heart
     (snippets/cl-card-wish.liquid), which asks for the `spin` variant.

     `spin` is the heart's: it swings against the direction of travel, twice, and then
     rolls a full turn into the header. A cart does not tumble — a box of something
     arrives level — so the cart callers leave it off and keep the plain arc.

     THE FLYER IS APPENDED TO <body>. Measured on this theme: the agency's
     `.shopify-section { position: relative; z-index: 1 }` makes each section its own
     stacking context, so a fixed element drawn from inside one is clamped there and
     the sticky header (a sibling section at z-index 20) paints over it — the icon
     would vanish behind the very thing it was flying to.

     Web Animations rather than a keyframe: the distance is only known at click time,
     since it depends on where the button and the header are on this screen at this
     width, and a keyframe cannot take a computed translate. No animate() means no
     flight, which is a missing flourish and not a broken button.

     opts: {
       icon      required — a URL, drawn as a mask so `color` applies
       target    CSS selector for the header icon to fly to (default: the cart)
       color, size, ms
       spin      swing-then-roll instead of a level arc
       minWidth  skip below this viewport width — for a target that is not in the
                 header at every breakpoint
     } */
  var FLY_CLASS = 'cl-atc-fly';
  var BUMP_CLASS = 'cl-atc-bump';
  var CART_SEL = '.cl-header__icon-link--cart';

  function flyToCart(from, opts) {
    opts = opts || {};
    if (!opts.icon || !from || !from.getBoundingClientRect) return;
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    /* Some header icons only exist at some widths — the wishlist moves into the burger
       panel below 1024px, so a flight to it there would travel to a hidden element. The
       rect test below would catch that anyway; this states the intent. */
    if (opts.minWidth && window.innerWidth < opts.minWidth) return;

    var target = document.querySelector(opts.target || CART_SEL);
    if (!target) return;
    var a = from.getBoundingClientRect();
    var b = target.getBoundingClientRect();
    /* A header scrolled out of view, or a hidden button, would send the icon to a corner. */
    if (!a.width || !b.width) return;

    var size = Number(opts.size) || 44;
    var ms = Number(opts.ms) || 900;
    var fly = document.createElement('span');
    fly.className = FLY_CLASS;
    fly.setAttribute('aria-hidden', 'true');
    fly.style.width = size + 'px';
    fly.style.height = size + 'px';
    fly.style.left = a.left + a.width / 2 - size / 2 + 'px';
    fly.style.top = a.top + a.height / 2 - size / 2 + 'px';
    /* Masked rather than an <img>, the way every icon in this theme is drawn, so the
       colour is a setting instead of being baked into the file. */
    var mask = "url('" + opts.icon + "') center/contain no-repeat";
    fly.style.webkitMask = mask;
    fly.style.mask = mask;
    fly.style.background = opts.color || 'var(--cl-text, #2C2C2D)';
    document.body.appendChild(fly);

    if (!fly.animate) { fly.remove(); return; }

    var dx = b.left + b.width / 2 - (a.left + a.width / 2);
    var dy = b.top + b.height / 2 - (a.top + a.height / 2);
    /* The arc. A straight line between two points that are nearly vertical of each
       other reads as the icon being dragged rather than thrown, so the midpoint is
       lifted — scaled to the distance, and capped so a short hop on a phone does not
       loop over the header. */
    var lift = Math.min(150, Math.max(50, Math.abs(dx) * 0.3 + Math.abs(dy) * 0.12 + 50));

    /* Rotation per keyframe. The swing leads AGAINST the travel — the way anything on a
       string lags behind the hand that threw it — and only then does it roll, so the two
       read as one gesture rather than as a spin bolted onto an arc. Level for the cart. */
    var r = opts.spin
      ? ['0deg', '-18deg', '14deg', '-8deg', '200deg', '360deg']
      : ['0deg', '0deg', '0deg', '0deg', '0deg', '0deg'];
    function t(x, y, sc, i) {
      return 'translate(' + x + 'px, ' + y + 'px) scale(' + sc + ') rotate(' + r[i] + ')';
    }

    var anim = fly.animate(
      [
        { transform: t(0, 0, 0.5, 0), opacity: 0, offset: 0 },
        { transform: t(dx * 0.12, dy * 0.12 - lift * 0.6, 1.35, 1), opacity: 1, offset: 0.2 },
        { transform: t(dx * 0.34, dy * 0.34 - lift * 0.92, 1.45, 2), opacity: 1, offset: 0.38 },
        { transform: t(dx * 0.5, dy * 0.5 - lift, 1.5, 3), opacity: 1, offset: 0.5 },
        { transform: t(dx * 0.86, dy * 0.86 - lift * 0.34, 0.8, 4), opacity: 1, offset: 0.82 },
        { transform: t(dx, dy, 0.2, 5), opacity: 0.85, offset: 1 }
      ],
      { duration: ms, easing: 'cubic-bezier(0.37, 0, 0.63, 1)', fill: 'forwards' }
    );

    function land() {
      fly.remove();
      /* Re-added after a reflow read so a second add during the first flight still
         plays the bump — removed and re-added in one frame, the keyframe never
         restarts. */
      target.classList.remove(BUMP_CLASS);
      void target.offsetWidth;
      target.classList.add(BUMP_CLASS);
      setTimeout(function () { target.classList.remove(BUMP_CLASS); }, 520);
    }
    if (anim.finished && anim.finished.then) anim.finished.then(land, land);
    else anim.onfinish = land;
    /* A tab backgrounded mid-flight can leave the animation unfinished, and a stray
       icon must not be left pinned to the page. */
    setTimeout(function () { if (fly.parentNode) fly.remove(); }, ms + 400);
  }

  /* ── Smooth <details> ─────────────────────────────────────────────────────
     A native <details> snaps: the browser has no state between closed and open, so
     there is nothing to transition. The panel is therefore animated by hand and the
     `open` attribute is set AFTER the opening animation starts and BEFORE the closing
     one — the attribute is what carries the accessibility semantics and the print
     rendering, so it is never faked with a class.

     Opt in with `data-cl-smooth` on the <details>, or by being one of the selectors
     below. Delegated from document so a section rendered later needs no setup.

     Height AND padding are animated together. Height alone leaves the body's 18px
     bottom padding on screen at height 0, which reads as the panel refusing to shut.

     The duration comes from the --cl-acc-ms custom property, so a merchant's setting
     governs the panel and the chevron from ONE value — the CSS transition on the
     chevron reads the same property. It is read per element, not cached, because it is
     inherited from the section wrapper and different sections may set it differently.

     Reduced motion is left to the browser entirely: no preventDefault, so the toggle
     is instant and native. */
  var DETAILS_SEL = 'details[data-cl-smooth], details.cl-pdp__acc';
  var DETAILS_FALLBACK_MS = 450;

  function detailsMs(el) {
    var raw = '';
    try {
      raw = getComputedStyle(el).getPropertyValue('--cl-acc-ms').trim();
    } catch (e) {}
    if (!raw) return DETAILS_FALLBACK_MS;
    var n = parseFloat(raw);
    if (!isFinite(n) || n <= 0) return DETAILS_FALLBACK_MS;
    /* A time token may be written either way; `0.45s` must not become 0.45ms. */
    return raw.indexOf('ms') > -1 ? n : n * 1000;
  }

  function animateDetails(det, summary, open, ms) {
    var body = summary.nextElementSibling;
    if (!body || !body.animate) {
      det.open = open;
      return;
    }
    det.dataset.clAnim = '1';

    /* Measured with the panel OPEN in both directions — a closed <details> has no laid-out
       body to measure, so opening has to reveal it first and animate from zero. */
    if (open) det.open = true;
    var cs = getComputedStyle(body);
    var h = body.scrollHeight;
    var pt = cs.paddingTop;
    var pb = cs.paddingBottom;
    var shut = { height: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0 };
    var full = { height: h + 'px', paddingTop: pt, paddingBottom: pb, opacity: 1 };

    body.style.overflow = 'hidden';
    var anim = body.animate([open ? shut : full, open ? full : shut], {
      duration: ms,
      easing: open ? 'cubic-bezier(0.22, 1, 0.36, 1)' : 'cubic-bezier(0.4, 0, 0.68, 0.06)',
      fill: 'both'
    });

    function end() {
      anim.cancel();
      body.style.overflow = '';
      /* Closing sets the attribute LAST, so the panel is still rendered while it collapses. */
      if (!open) det.open = false;
      delete det.dataset.clAnim;
    }
    if (anim.finished && anim.finished.then) anim.finished.then(end, end);
    else anim.onfinish = end;
  }

  document.addEventListener('click', function (e) {
    var summary = e.target.closest ? e.target.closest('summary') : null;
    if (!summary) return;
    var det = summary.parentElement;
    if (!det || !det.matches || !det.matches(DETAILS_SEL)) return;
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    /* A tap mid-animation is swallowed rather than queued: reversing halfway needs the
       height it was actually at, and a queued toggle would fight the running animation. */
    if (det.dataset.clAnim === '1') {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    animateDetails(det, summary, !det.open, detailsMs(det));
  });

  window.clMotion = {
    register: safeRegister,
    revealAll: revealAll,
    collapse: collapse,
    release: release,
    /* One function, two names: `fly` says what it does now that the target is an option,
       and flyToCart stays because three call sites and the PDP's own settings use it. */
    fly: flyToCart,
    flyToCart: flyToCart,
    details: animateDetails,
    COLLAPSE_MS: COLLAPSE_MS
  };
})();
